import { AppState } from '../reducers';

// services
import * as Services from '../../services/ContractReview.Services';

// models
import {
  ContractReviewFinding,
  FindingDecision,
  NewContractReview
} from '../../models/Entities/Contracts/ContractReview';
import { ErrorType } from '../../models/HttpError';

// functions
import {
  buildDecisionItems,
  chunkDecisions,
  normalizeFinding,
  normalizeReview
} from '../../components/Contracts/ContractReview/ContractReview.Functions';

// actions
import * as Actions from '../actions/ContractReview.Actions';

// Revisión de contratos con IA (spec 012). Mismo patrón que ContractIAImportMiddleware: el token sale del estado y,
// sin token, la acción falla con 401. Los thunks devuelven su promesa para poder esperarlos.

type Dispatch = (action: Actions.ContractReviewKnownAction | any) => void;
type ReviewThunk<T = void> = (dispatch: Dispatch, getState: () => AppState) => Promise<T>;

const NotAuthorized: ErrorType = { ErrorCode: 401, ErrorMessage: 'Not authorized', Errors: [] };

const tokenOf = (getState: () => AppState): string | null => getState()?.oidc?.user?.access_token ?? null;

const CreateContractReview =
  (contractId: number, command: NewContractReview): ReviewThunk =>
  async (dispatch, getState) => {
    const token = tokenOf(getState);
    if (token === null) {
      dispatch({ type: Actions.CONTRACT_REVIEW_CREATE_FAILURE, contractId, error: NotAuthorized });
      return;
    }

    dispatch({ type: Actions.CONTRACT_REVIEW_CREATE_REQUEST, contractId, command });
    try {
      const review = await Services.CreateContractReview(contractId, command, token);
      dispatch({ type: Actions.CONTRACT_REVIEW_CREATE_SUCCESS, contractId, review: review ? normalizeReview(review) : undefined });
      // Sin cuerpo (tolerancia): se recarga el historial para mostrar la revisión nueva.
      if (!review) await GetContractReviews(contractId)(dispatch, getState);
    } catch (error) {
      dispatch({ type: Actions.CONTRACT_REVIEW_CREATE_FAILURE, contractId, error: error as ErrorType });
      // 409: hay una revisión en curso; el historial la muestra para ofrecer "Ver revisión".
      if ((error as ErrorType)?.ErrorCode === 409) await GetContractReviews(contractId)(dispatch, getState);
    }
  };

const GetContractReviews =
  (contractId: number): ReviewThunk =>
  async (dispatch, getState) => {
    const token = tokenOf(getState);
    if (token === null) {
      dispatch({ type: Actions.CONTRACT_REVIEW_GETALL_FAILURE, contractId, error: NotAuthorized });
      return;
    }

    dispatch({ type: Actions.CONTRACT_REVIEW_GETALL_REQUEST, contractId });
    try {
      const reviews = await Services.GetContractReviews(contractId, token);
      dispatch({ type: Actions.CONTRACT_REVIEW_GETALL_SUCCESS, contractId, reviews: reviews.map(normalizeReview) });
    } catch (error) {
      dispatch({ type: Actions.CONTRACT_REVIEW_GETALL_FAILURE, contractId, error: error as ErrorType });
    }
  };

const GetContractReviewById =
  (reviewId: string): ReviewThunk =>
  async (dispatch, getState) => {
    const token = tokenOf(getState);
    if (token === null) {
      dispatch({ type: Actions.CONTRACT_REVIEW_GETBYID_FAILURE, reviewId, error: NotAuthorized });
      return;
    }

    dispatch({ type: Actions.CONTRACT_REVIEW_GETBYID_REQUEST, reviewId });
    try {
      const review = await Services.GetContractReviewById(reviewId, token);
      dispatch({ type: Actions.CONTRACT_REVIEW_GETBYID_SUCCESS, reviewId, review: normalizeReview(review) });
    } catch (error) {
      dispatch({ type: Actions.CONTRACT_REVIEW_GETBYID_FAILURE, reviewId, error: error as ErrorType });
    }
  };

/** Decisión individual, optimista: el REQUEST lleva la decisión anterior para revertirla si falla. */
const UpdateFindingDecision =
  (reviewId: string, findingId: number, decision: FindingDecision, comment?: string | null): ReviewThunk =>
  async (dispatch, getState) => {
    const previous: ContractReviewFinding | undefined = getState()?.ContractReview?.byId?.[reviewId]?.findings?.find(f => f.id === findingId);
    const previousDecision: FindingDecision = previous?.decision ?? 'Pending';
    const previousComment = previous?.decisionComment ?? null;
    const normalizedComment = comment?.trim() ? comment.trim() : null;

    const token = tokenOf(getState);
    if (token === null) {
      dispatch({ type: Actions.CONTRACT_REVIEW_DECISION_FAILURE, reviewId, findingId, previousDecision, previousComment, error: NotAuthorized });
      return;
    }

    dispatch({
      type: Actions.CONTRACT_REVIEW_DECISION_REQUEST,
      reviewId,
      findingId,
      decision,
      comment: normalizedComment,
      previousDecision,
      previousComment
    });
    try {
      const finding = await Services.UpdateFindingDecision(findingId, { decision, comment: normalizedComment }, token);
      dispatch({ type: Actions.CONTRACT_REVIEW_DECISION_SUCCESS, reviewId, findingId, finding: finding ? normalizeFinding(finding) : undefined });
    } catch (error) {
      dispatch({ type: Actions.CONTRACT_REVIEW_DECISION_FAILURE, reviewId, findingId, previousDecision, previousComment, error: error as ErrorType });
    }
  };

/**
 * Decisión en bloque (DEC-004): un `PATCH contracts/reviews/{ReviewId}/findings` por lote de 200, en secuencia; cada
 * lote es todo o nada y el primero que falla detiene el resto y recarga la revisión. Devuelve cuántos se actualizaron.
 */
const UpdateFindingDecisionBulk =
  (reviewId: string, findingIds: number[], decision: FindingDecision, comment?: string | null, onProgress?: (done: number, total: number) => void): ReviewThunk<number> =>
  async (dispatch, getState) => {
    const token = tokenOf(getState);
    if (token === null) {
      dispatch({ type: Actions.CONTRACT_REVIEW_DECISION_BULK_FAILURE, reviewId, error: NotAuthorized });
      return 0;
    }

    let updated = 0;
    const batches = chunkDecisions(buildDecisionItems(findingIds, decision, comment));
    for (const items of batches) {
      dispatch({ type: Actions.CONTRACT_REVIEW_DECISION_BULK_REQUEST, reviewId, items });
      try {
        const findings = await Services.UpdateFindingDecisionBulk(reviewId, items, token);
        dispatch({ type: Actions.CONTRACT_REVIEW_DECISION_BULK_SUCCESS, reviewId, findings: findings.map(normalizeFinding) });
        updated += items.length;
        onProgress?.(updated, findingIds.length);
      } catch (error) {
        dispatch({ type: Actions.CONTRACT_REVIEW_DECISION_BULK_FAILURE, reviewId, error: error as ErrorType });
        await GetContractReviewById(reviewId)(dispatch, getState);
        break;
      }
    }
    return updated;
  };

const ApplyContractReview =
  (reviewId: string): ReviewThunk =>
  async (dispatch, getState) => {
    const token = tokenOf(getState);
    if (token === null) {
      dispatch({ type: Actions.CONTRACT_REVIEW_APPLY_FAILURE, reviewId, error: NotAuthorized });
      return;
    }

    dispatch({ type: Actions.CONTRACT_REVIEW_APPLY_REQUEST, reviewId });
    try {
      const result = await Services.ApplyContractReview(reviewId, token);
      dispatch({ type: Actions.CONTRACT_REVIEW_APPLY_SUCCESS, reviewId, result });
      if (!result) await GetContractReviewById(reviewId)(dispatch, getState);
    } catch (error) {
      dispatch({ type: Actions.CONTRACT_REVIEW_APPLY_FAILURE, reviewId, error: error as ErrorType });
      // Fuera de 400/409 el estado real es incierto: se recarga la revisión.
      const code = (error as ErrorType)?.ErrorCode;
      if (code !== 400 && code !== 409) await GetContractReviewById(reviewId)(dispatch, getState);
    }
  };

const GetContractParametrizationStatus =
  (contractId: number): ReviewThunk =>
  async (dispatch, getState) => {
    const token = tokenOf(getState);
    if (token === null) {
      dispatch({ type: Actions.CONTRACT_REVIEW_PARAMETRIZATION_FAILURE, contractId, error: NotAuthorized });
      return;
    }

    dispatch({ type: Actions.CONTRACT_REVIEW_PARAMETRIZATION_REQUEST, contractId });
    try {
      const status = await Services.GetContractParametrizationStatus(contractId, token);
      dispatch({ type: Actions.CONTRACT_REVIEW_PARAMETRIZATION_SUCCESS, contractId, status });
    } catch (error) {
      dispatch({ type: Actions.CONTRACT_REVIEW_PARAMETRIZATION_FAILURE, contractId, error: error as ErrorType });
    }
  };

/**
 * Deshace un hallazgo aplicado (spec 015; Contracts 021 §4.1). Haya salido bien o no, se recarga el detalle: el
 * estado y los contadores de la revisión cambian, y en un 409 el hallazgo guarda `undoMessage`.
 */
const UndoFinding =
  (reviewId: string, findingId: number): ReviewThunk =>
  async (dispatch, getState) => {
    const token = tokenOf(getState);
    if (token === null) {
      dispatch({ type: Actions.CONTRACT_REVIEW_UNDO_FINDING_FAILURE, reviewId, findingId, error: NotAuthorized });
      return;
    }

    dispatch({ type: Actions.CONTRACT_REVIEW_UNDO_FINDING_REQUEST, reviewId, findingId });
    try {
      const finding = await Services.UndoFinding(findingId, token);
      dispatch({ type: Actions.CONTRACT_REVIEW_UNDO_FINDING_SUCCESS, reviewId, findingId, finding: finding ? normalizeFinding(finding) : undefined });
    } catch (error) {
      dispatch({ type: Actions.CONTRACT_REVIEW_UNDO_FINDING_FAILURE, reviewId, findingId, error: error as ErrorType });
    }
    await GetContractReviewById(reviewId)(dispatch, getState);
  };

/** Deshace todos los cambios aplicados de la revisión (spec 015; Contracts 021 §4.2) y recarga el detalle. */
const UndoContractReview =
  (reviewId: string): ReviewThunk =>
  async (dispatch, getState) => {
    const token = tokenOf(getState);
    if (token === null) {
      dispatch({ type: Actions.CONTRACT_REVIEW_UNDO_FAILURE, reviewId, error: NotAuthorized });
      return;
    }

    dispatch({ type: Actions.CONTRACT_REVIEW_UNDO_REQUEST, reviewId });
    try {
      const result = await Services.UndoContractReview(reviewId, token);
      dispatch({ type: Actions.CONTRACT_REVIEW_UNDO_SUCCESS, reviewId, result });
    } catch (error) {
      dispatch({ type: Actions.CONTRACT_REVIEW_UNDO_FAILURE, reviewId, error: error as ErrorType });
    }
    await GetContractReviewById(reviewId)(dispatch, getState);
  };

const ClearUndoResult = () => (dispatch: Dispatch) => dispatch({ type: Actions.CONTRACT_REVIEW_CLEAR_UNDO_RESULT });

const ClearCreateStatus = () => (dispatch: Dispatch) => dispatch({ type: Actions.CONTRACT_REVIEW_CLEAR_CREATE_STATUS });

const ClearApplyResult = () => (dispatch: Dispatch) => dispatch({ type: Actions.CONTRACT_REVIEW_CLEAR_APPLY_RESULT });

export const actionCreators = {
  CreateContractReview,
  GetContractReviews,
  GetContractReviewById,
  UpdateFindingDecision,
  UpdateFindingDecisionBulk,
  ApplyContractReview,
  GetContractParametrizationStatus,
  UndoFinding,
  UndoContractReview,
  ClearUndoResult,
  ClearCreateStatus,
  ClearApplyResult
};
